import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tab, Tabs,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import {
  useCreateRoleRequirement, useCreateTaskRequirement,
  useDeleteRoleRequirement, useDeleteTaskRequirement,
  useDocumentRequirements,
} from "@/api/hooks/documentRequirements";
import {
  DOCUMENT_TYPE_LABELS, DOCUMENT_TYPES, PERSONNEL_ROLE_LABELS, PERSONNEL_ROLES,
} from "@/api/labels";
import type {
  DocumentType, PersonnelRole, RoleDocumentRequirement,
  TaskDocumentRequirement,
} from "@/api/types";

/**
 * Document requirements editor. Two tabs:
 * - "By role" — role + document_type rows
 * - "By task" — delegation-log task code + document_type rows
 *
 * Each tab carries a create form on top and the existing rows below.
 * Delete is hard-delete (no soft delete on these tables).
 */
export function DocumentRequirementsPage() {
  const [tab, setTab] = useState<"role" | "task">("role");
  const query = useDocumentRequirements();
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Document requirements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drives the per-person doc-status modal. Role rules apply to
        every member of a canonical role; task rules apply to anyone
        delegated that task on a delegation log.
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as "role" | "task")}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab value="role" label="By role" sx={{ textTransform: "none" }} />
        <Tab value="task" label="By delegated task" sx={{ textTransform: "none" }} />
      </Tabs>

      {query.isLoading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {query.error && (
        <Alert severity="error">
          Could not load requirements: {(query.error as Error).message}
        </Alert>
      )}
      {query.data && (
        tab === "role"
          ? <RoleTable rows={query.data.by_role} />
          : <TaskTable rows={query.data.by_task} />
      )}
    </Box>
  );
}

function RoleTable({ rows }: { rows: RoleDocumentRequirement[] }) {
  const create = useCreateRoleRequirement();
  const remove = useDeleteRoleRequirement();
  const [role, setRole] = useState<PersonnelRole>("pi");
  const [docType, setDocType] = useState<DocumentType>("investigator_cv");
  const [notes, setNotes] = useState("");

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "center" }, mb: 3 }}
      >
        <TextField
          select size="small" label="Role" value={role}
          onChange={(e) => setRole(e.target.value as PersonnelRole)}
          sx={{ minWidth: 220 }}
        >
          {PERSONNEL_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {PERSONNEL_ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Document" value={docType}
          onChange={(e) => setDocType(e.target.value as DocumentType)}
          sx={{ minWidth: 260 }}
        >
          {DOCUMENT_TYPES.map((dt) => (
            <MenuItem key={dt} value={dt}>{DOCUMENT_TYPE_LABELS[dt]}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Notes (optional)" value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Button
          variant="contained"
          disabled={create.isPending}
          onClick={() => create.mutate(
            { role, document_type: docType, notes: notes.trim() || null },
            { onSuccess: () => setNotes("") },
          )}
        >
          {create.isPending ? "Adding…" : "Add rule"}
        </Button>
      </Stack>
      {create.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {create.error.message}
        </Alert>
      )}
      {rows.length === 0 ? (
        <Typography color="text.secondary">No role rules yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  {PERSONNEL_ROLE_LABELS[r.role as PersonnelRole] ?? r.role}
                </TableCell>
                <TableCell>
                  {DOCUMENT_TYPE_LABELS[r.document_type as DocumentType] ?? r.document_type}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {r.notes ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (window.confirm("Delete this rule?")) remove.mutate(r.id);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function TaskTable({ rows }: { rows: TaskDocumentRequirement[] }) {
  const create = useCreateTaskRequirement();
  const remove = useDeleteTaskRequirement();
  const [taskCode, setTaskCode] = useState("");
  const [taskLabel, setTaskLabel] = useState("");
  const [docType, setDocType] = useState<DocumentType>("iata");
  const [notes, setNotes] = useState("");

  const onCreate = () => {
    if (!taskCode.trim() || !taskLabel.trim()) return;
    create.mutate(
      {
        task_code: taskCode.trim(),
        task_label: taskLabel.trim(),
        document_type: docType,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setTaskCode("");
          setTaskLabel("");
          setNotes("");
        },
      },
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        sx={{ alignItems: { md: "center" }, mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          size="small" label="Task code" placeholder="5"
          value={taskCode} onChange={(e) => setTaskCode(e.target.value)}
          sx={{ width: 120 }}
        />
        <TextField
          size="small" label="Task label" placeholder="Blood Sample Shipping"
          value={taskLabel} onChange={(e) => setTaskLabel(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select size="small" label="Document" value={docType}
          onChange={(e) => setDocType(e.target.value as DocumentType)}
          sx={{ minWidth: 260 }}
        >
          {DOCUMENT_TYPES.map((dt) => (
            <MenuItem key={dt} value={dt}>{DOCUMENT_TYPE_LABELS[dt]}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Notes (optional)" value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          variant="contained"
          disabled={create.isPending || !taskCode.trim() || !taskLabel.trim()}
          onClick={onCreate}
        >
          {create.isPending ? "Adding…" : "Add rule"}
        </Button>
      </Stack>
      {create.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {create.error.message}
        </Alert>
      )}
      {rows.length === 0 ? (
        <Typography color="text.secondary">No task rules yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 100 }}>Code</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {r.task_code}
                </TableCell>
                <TableCell>{r.task_label}</TableCell>
                <TableCell>
                  {DOCUMENT_TYPE_LABELS[r.document_type as DocumentType] ?? r.document_type}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {r.notes ?? "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (window.confirm("Delete this rule?")) remove.mutate(r.id);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
